import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "page-body" }
const _hoisted_2 = ["innerHTML"]
const _hoisted_3 = {
  key: 1,
  class: "page-content"
}
const _hoisted_4 = { class: "company" }
const _hoisted_5 = {
  key: 0,
  class: "brand"
}
const _hoisted_6 = ["src"]
const _hoisted_7 = { class: "infos" }
const _hoisted_8 = {
  key: 0,
  class: "name"
}
const _hoisted_9 = {
  key: 1,
  class: "document"
}
const _hoisted_10 = {
  key: 2,
  class: "address"
}
const _hoisted_11 = {
  key: 3,
  class: "address"
}
const _hoisted_12 = {
  key: 2,
  class: "page-content"
}
const _hoisted_13 = { class: "analyse" }
const _hoisted_14 = ["innerHTML"]
const _hoisted_15 = {
  key: 3,
  class: "page-content"
}
const _hoisted_16 = { class: "employee" }
const _hoisted_17 = ["innerHTML"]
const _hoisted_18 = {
  key: 4,
  class: "page-content"
}
const _hoisted_19 = { class: "methods" }
const _hoisted_20 = ["innerHTML"]
const _hoisted_21 = {
  key: 5,
  class: "page-content"
}
const _hoisted_22 = { class: "forms" }
const _hoisted_23 = ["innerHTML"]
const _hoisted_24 = {
  key: 6,
  class: "page-content"
}
const _hoisted_25 = { class: "samples" }
const _hoisted_26 = ["innerHTML"]
const _hoisted_27 = {
  key: 7,
  class: "page-content"
}
const _hoisted_28 = { class: "equipments" }
const _hoisted_29 = ["innerHTML"]
const _hoisted_30 = {
  key: 8,
  class: "page-content"
}
const _hoisted_31 = { class: "signatures" }
const _hoisted_32 = { class: "image" }
const _hoisted_33 = ["src"]
const _hoisted_34 = ["src"]
const _hoisted_35 = { class: "name" }
const _hoisted_36 = { class: "type" }
const _hoisted_37 = { class: "date" }
const _hoisted_38 = { class: "document" }

import { computed, ref, onMounted, watch } from "vue";
import { marked } from "marked";
import { useI18n } from "vue-i18n";

import {
  getDate,
  getDateAndHour,
  getDifference,
  getDiffBreaks,
} from "@/common/utils/dates";
import { paginateText, getMarkdownBlockHeights } from "@/common/utils/text";

import { fileGet } from "@/common/api/rest/files";

import type { AnalysisFullType } from "@/common/types/analysis";

import { pageExport } from "@/components";

interface ContentType {
  title: string | null;
  type: string;
  break: boolean;
  content: any;
}

interface PageType {
  page: number;
  content: {
    [key: string]: ContentType | null;
  } | null;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  props: {
    data: {},
    width: {},
    scale: {},
    footer: {},
    filters: {}
  },
  setup(__props: any) {

const props = __props;

const { t, tm } = useI18n();

const width = ref("210mm");
// Altura útil da página A4 (297mm - margens) convertida para pixels aproximados
// Considerando ~3.78 pixels por mm: 297mm = ~1122px, menos margens e footer
const height = ref(950); // Aumentado para melhor aproveitamento
const heightTitle = ref(50); // Reduzido para economizar espaço
const minSpaceForContent = ref(100); // Espaço mínimo necessário para iniciar novo conteúdo

const pages = ref<PageType[]>([]);

const summary = computed(
  () => tm("report.types.summary") as Record<string, string>,
);

async function paginate() {
  if (!props.data) return;

  pages.value = [];
  let PAGE = 0;
  let HEIGHT_INTERNAL = 0;

  pages.value.push({
    page: PAGE,
    content: {},
  });

  const paginateTitle = () => {
    let title = "";

    title += `<strong>${t("report.labels.evaluation").toUpperCase()}:</strong>`;
    title += `&nbsp; ${props.data.evaluation.code}`;
    title += "&nbsp; &nbsp; &nbsp; &nbsp;";
    title += `<strong>${t("report.labels.analyse").toUpperCase()}:</strong>`;
    title += `&nbsp; ${props.data.code}`;

    pages.value[PAGE].content = {
      ...pages.value[PAGE].content,
      title: {
        title,
        type: "TITLE",
        break: false,
        content: "",
      },
    };

    HEIGHT_INTERNAL += heightTitle.value;
  };

  const newPage = (field: string, type: string, title: string | null) => {
    PAGE++;
    pages.value[PAGE] = {
      page: PAGE,
      content: {},
    };
    HEIGHT_INTERNAL = 0;
    paginateTitle();

    pages.value[PAGE].content = {
      ...pages.value[PAGE].content,
      [field]: {
        title,
        type,
        break: false,
        content: "",
      },
    };
  };

  const hasSpaceForContent = (requiredHeight: number): boolean => {
    return HEIGHT_INTERNAL + requiredHeight <= height.value;
  };

  const paginateCompany = () => {
    if (!props.data?.company) return;

    const HEITHG_COMPANY_AVATAR = 90;
    const COMPANY_MARGIN = 20; // Margem de segurança

    const {
      avatar,
      name,
      document_name,
      document_number,
      address: addresses,
      address_id,
      contacts: contactsAll,
      contact_id,
    } = props.data!.company || {};

    // Calcular altura total necessária
    const companyNameInfos = paginateText(name?.toUpperCase(), {
      width: "601px",
      height: height.value,
      font_size: "14px",
      line_height: "normal",
    });

    let documentText = "";
    if (document_name) documentText += document_name;
    if (document_number) documentText += ` ${document_number}`;

    const companyDocumentInfos = paginateText(documentText.toUpperCase(), {
      width: "601px",
      height: height.value,
      font_size: "14px",
      line_height: "normal",
    });

    const address = addresses?.filter(
      (item) => item.address_id === address_id,
    )?.[0];

    let addressText = "";
    if (address?.address) addressText += address.address;
    if (address?.number) addressText += `, ${address.number}`;
    if (address?.complement) addressText += `, ${address.complement}`;
    if (address?.district) addressText += `, ${address.district}`;
    if (address?.city) addressText += `, ${address.city}`;
    if (address?.state) addressText += `, ${address.state}`;
    if (address?.country) addressText += `, ${address.country}`;
    if (address?.cep) addressText += `, CEP ${address.cep}`;

    const companyAdressInfos = paginateText(addressText.toUpperCase(), {
      width: "601px",
      height: height.value,
      font_size: "12px",
      line_height: "normal",
    });

    const contacts = contactsAll?.filter(
      (item) => item.contact_id === contact_id,
    )?.[0];

    let contactText = "";
    if (contacts?.name) contactText += contacts.name;
    if (contacts?.contact) contactText += ` - ${contacts.contact}`;

    const companyContactInfos = paginateText(contactText.toUpperCase(), {
      width: "601px",
      height: height.value,
      font_size: "12px",
      line_height: "normal",
    });

    const companyNameHeight = companyNameInfos
      .map((item) => item.height)
      .reduce((prev, next) => prev + next, 0);

    const companyDocumentHeight = companyDocumentInfos
      .map((item) => item.height)
      .reduce((prev, next) => prev + next, 0);

    const companyAdressHeight = companyAdressInfos
      .map((item) => item.height)
      .reduce((prev, next) => prev + next, 0);

    const companyContactHeight = companyContactInfos
      .map((item) => item.height)
      .reduce((prev, next) => prev + next, 0);

    const HEITHG_COMPANY_INFOS =
      companyNameHeight +
      companyDocumentHeight +
      companyAdressHeight +
      companyContactHeight +
      12; // gaps entre linhas

    const HEIGHT_TOTAL =
      Math.max(HEITHG_COMPANY_AVATAR, HEITHG_COMPANY_INFOS) + COMPANY_MARGIN;

    // Verificar se há espaço suficiente
    if (!hasSpaceForContent(heightTitle.value + HEIGHT_TOTAL)) {
      newPage("company", "COMPANY", null);
    } else {
      HEIGHT_INTERNAL += heightTitle.value;
    }

    pages.value[PAGE].content = {
      ...pages.value[PAGE].content,
      company: {
        title: null,
        type: "COMPANY",
        break: false,
        content: {
          avatar: avatar,
          name: companyNameInfos[0]?.text,
          document: companyDocumentInfos[0]?.text,
          address: companyAdressInfos[0]?.text,
          contact: companyContactInfos[0]?.text,
        },
      },
    };

    HEIGHT_INTERNAL += HEIGHT_TOTAL;
  };

  const paginateAnalyse = () => {
    if (!props.data) return;

    const analyse = props.data;

    let markText = "";

    markText += `#### ${t("report.labels.evaluation").toUpperCase()} ${analyse.evaluation.code}`;
    markText += ` &nbsp; | &nbsp; ${t("report.labels.analyse").toUpperCase()} ${analyse.code}\n`;
    markText += `${analyse.description ?? ""}\n\n`;

    markText += `- **${t("report.labels.status")}:** &nbsp; ${t(`enums.analysis_status.${analyse.status}`).toUpperCase()}\n`;

    if (analyse.start_date) {
      const diffTime = getDiffBreaks(
        analyse.start_edited ?? analyse.start_date,
        analyse.end_date
          ? (analyse.end_edited ?? analyse.end_date)
          : new Date().toISOString(),
        analyse.breaks ?? [],
      );
      markText += `- **${t("report.labels.start")}:** &nbsp; ${getDateAndHour(analyse.start_edited ?? analyse.start_date)}`;
      markText += `&nbsp; | &nbsp; **${t("report.labels.end")}:** &nbsp; ${getDateAndHour(analyse.end_edited ?? analyse.end_date)}`;
      markText += `&nbsp; | &nbsp; **${t("report.labels.elapsed_time")}:** &nbsp; ${diffTime.text}\n`;
    } else {
      markText += `- **${t("report.labels.start")}:** &nbsp; --/--/---- --:--:--`;
      markText += `&nbsp; | &nbsp; **${t("report.labels.end")}:** &nbsp; --/--/---- --:--:--`;
      markText += `&nbsp; | &nbsp; **${t("report.labels.elapsed_time")}:** 0m 0s\n`;
    }

    if (analyse.technical) {
      markText += `- **${t("report.labels.technical")}:**`;
      markText += ` &nbsp; ${analyse.technical.name}\n`;
    }

    if (analyse.assistant) {
      if (analyse.technical) {
        markText += "&nbsp; | &nbsp; ";
      } else {
        markText += "- ";
      }

      markText += `**${t("report.labels.assistant")}:**`;
      markText += ` &nbsp; ${analyse.assistant.name}\n`;
    } else {
      markText += "\n";
    }

    markText += `- **${t("report.labels.degree_of_risk")}:** &nbsp; ${analyse.danger}\n`;

    if (analyse.status === "CANCELED") {
      markText += `#### ${t("report.labels.cancellation")}\n`;
      markText += `- **${t("report.labels.date")}:** &nbsp; ${getDateAndHour(analyse.canceled_date)} \n`;
      markText += `- **${t("report.labels.motive")}:** &nbsp; ${analyse.canceled_description ?? ""}\n`;
    }

    if (analyse.breaks?.length) {
      markText += "---\n";
      markText += `#### ${t("report.labels.pauses").toUpperCase()}\n\n`;
      analyse.breaks.forEach((item, index) => {
        const pauseDiffTime = getDifference(
          item.start_date,
          item.end_date ? item.end_date : new Date().toISOString(),
        );

        markText += `&nbsp; &nbsp; **${index + 1} - ${item.motive}**\n\n`;
        if (item.description) {
          markText += `&nbsp; &nbsp; ${item.description}\n`;
        }
        markText += `- **${t("report.labels.start")}:** &nbsp; ${getDateAndHour(item.start_date)}`;
        markText += ` &nbsp; | &nbsp; **${t("report.labels.end")}:** &nbsp; ${getDateAndHour(item.end_date)}`;
        markText += ` &nbsp; | &nbsp; **${t("report.labels.elapsed_time")}:** &nbsp; ${pauseDiffTime.text}\n`;

        if ((analyse.breaks?.length ?? 0) > index + 1) {
          markText += "---\n";
        }
      });
    }

    // Verificar espaço antes de adicionar título
    if (!hasSpaceForContent(heightTitle.value + minSpaceForContent.value)) {
      newPage("analyse", "ANALYSE", t("report.titles.analyse"));
    } else {
      HEIGHT_INTERNAL += heightTitle.value;
      pages.value[PAGE].content = {
        ...pages.value[PAGE].content,
        analyse: {
          title: t("report.titles.analyse"),
          type: "ANALYSE",
          break: false,
          content: "",
        },
      };
    }

    let pageText = "";
    const lines = markText.split("\n");

    for (let i = 0; i < lines.length; i++) {
      const text = lines[i];
      const pragraph = getMarkdownBlockHeights(text, {
        width: "617px",
        font_size: "0.75em",
        line_height: "normal",
      });

      const blockHeight = (pragraph[0]?.height ?? 0) + 3; // margem entre linhas

      if (!hasSpaceForContent(blockHeight)) {
        if (pageText.trim()) {
          pages.value[PAGE]!.content!.analyse!.content = marked(pageText);
        }
        newPage("analyse", "ANALYSE", null);
        pageText = `${text}\n`;
        HEIGHT_INTERNAL += blockHeight;
      } else {
        HEIGHT_INTERNAL += blockHeight;
        pageText += `${text}\n`;
      }
    }

    if (pageText.trim()) {
      pages.value[PAGE]!.content!.analyse!.content = marked(pageText);
    }
  };

  const paginateEmployee = () => {
    if (!props.data?.employee) return;

    const employee = props.data.employee;

    let markText = "";

    if (employee.social_name) {
      markText += `#### ${employee.social_name}\n`;
    } else {
      markText += `#### ${employee.name}\n`;
    }
    markText += `${employee.document_name?.toUpperCase()} ${employee.document_number ?? ""}`;
    if (employee.registration_number) {
      markText += ` &nbsp; | &nbsp; ${t("report.labels.registration").toUpperCase()}: &nbsp; ${employee.registration_number ?? ""}`;
    }
    markText += "\n\n";

    markText += `${employee.description ?? ""}\n`;

    markText += `- **${t("report.labels.role")}:** &nbsp; ${employee.role ?? ""}\n`;
    markText += `- **${t("report.labels.sector")}:** &nbsp;  ${employee.sector ?? ""}\n`;

    if (employee.start_date) {
      markText += `- **${t("report.labels.bond")}:**`;
      markText += ` &nbsp; ${getDate(employee.start_date)}`;

      if (employee.end_date) {
        markText += ` - ${getDate(employee.end_date)}\n`;
      } else {
        markText += ` - ${t("report.labels.current")} \n`;
      }
    }

    if (employee.ges_name) {
      markText += `- **${t("report.labels.ges_name")}:**`;
      markText += ` &nbsp;  ${employee.ges_name ?? ""}`;
      markText += "\n";
    }

    if (employee.ges_status) {
      markText += `- **${t("report.labels.ges_status")}:**`;
      markText += `  &nbsp;  ${employee.ges_status ? t("common.labels." + employee.ges_status?.toLocaleLowerCase()) : ""}`;
      markText += "\n";
    }

    if (employee.ges_description) {
      markText += `- **${t("report.labels.ges_description")}:**`;
      markText += ` &nbsp; ${employee.ges_description ?? ""}\n`;
    }

    // Verificar espaço antes de adicionar título
    if (!hasSpaceForContent(heightTitle.value + minSpaceForContent.value)) {
      newPage("employee", "EMPLOYEE", t("report.titles.employee"));
    } else {
      HEIGHT_INTERNAL += heightTitle.value;
      pages.value[PAGE].content = {
        ...pages.value[PAGE].content,
        employee: {
          title: t("report.titles.employee"),
          type: "EMPLOYEE",
          break: false,
          content: "",
        },
      };
    }

    let pageText = "";
    markText.split("\n").forEach((text) => {
      const pragraph = getMarkdownBlockHeights(text, {
        width: "617px",
        font_size: "0.75em",
        line_height: "normal",
      });

      const blockHeight = (pragraph[0]?.height ?? 0) + 3;

      if (!hasSpaceForContent(blockHeight)) {
        if (pageText.trim()) {
          pages.value[PAGE]!.content!.employee!.content = marked(pageText);
        }
        newPage("employee", "EMPLOYEE", null);
        pageText = `${text}\n`;
        HEIGHT_INTERNAL += blockHeight;
      } else {
        HEIGHT_INTERNAL += blockHeight;
        pageText += `${text}\n`;
      }
    });

    if (pageText.trim()) {
      pages.value[PAGE]!.content!.employee!.content = marked(pageText);
    }
  };

  const paginateMethod = () => {
    if (!props.data?.methods?.length) return;

    // Verificar espaço antes de adicionar título
    if (!hasSpaceForContent(heightTitle.value + minSpaceForContent.value)) {
      newPage("methods", "METHODS", t("report.titles.methods"));
    } else {
      HEIGHT_INTERNAL += heightTitle.value;
      pages.value[PAGE].content = {
        ...pages.value[PAGE].content,
        methods: {
          title: t("report.titles.methods"),
          type: "METHODS",
          break: false,
          content: "",
        },
      };
    }

    let markText = "";

    props.data.methods?.forEach((item, index) => {
      markText += `#### ${item.code}\n`;
      markText += `<span style="font-size: 0.75em;">${item.name}</span>\n`;

      item.metadata?.forEach((field: any) => {
        if (field.type === "RANGE") {
          markText += `- **${field.name}:**`;
          if (field.min) {
            markText += ` &nbsp; ${field.min}`;
          }
          if (field.max) {
            markText += ` a ${field.max}`;
          }
          if (field.measure) {
            markText += ` ${field.measure}`;
          }
          markText += `\n`;
        } else {
          if (
            field.answer !== null &&
            field.answer !== undefined &&
            field.answer !== ""
          ) {
            markText += `- **${field.name}:** &nbsp; ${field.answer}\n`;
          }
        }
      });

      if ((props.data.methods?.length ?? 0) > index + 1) {
        markText += "---\n";
      }
    });

    let methodText = "";
    markText.split("\n").forEach((text) => {
      const pragraph = getMarkdownBlockHeights(text, {
        width: "617px",
        font_size: "0.75em",
        line_height: "normal",
      });

      const blockHeight = (pragraph[0]?.height ?? 0) + 6;

      if (!hasSpaceForContent(blockHeight)) {
        if (methodText.trim()) {
          pages.value[PAGE]!.content!.methods!.content = marked(methodText);
        }
        newPage("methods", "METHODS", null);
        methodText = `${text}\n`;
        HEIGHT_INTERNAL += blockHeight;
      } else {
        HEIGHT_INTERNAL += blockHeight;
        methodText += `${text}\n`;
      }
    });

    if (methodText.trim()) {
      pages.value[PAGE]!.content!.methods!.content = marked(methodText);
    }
  };

  const paginateForm = async () => {
    if (!props.data?.forms?.length && !props.data?.methods?.length) return;

    // Verificar espaço antes de adicionar título
    if (!hasSpaceForContent(heightTitle.value + minSpaceForContent.value)) {
      newPage("forms", "FORMS", t("report.titles.field_memory"));
    } else {
      HEIGHT_INTERNAL += heightTitle.value;
      pages.value[PAGE].content = {
        ...pages.value[PAGE].content,
        forms: {
          title: t("report.titles.field_memory"),
          type: "FORMS",
          break: false,
          content: "",
        },
      };
    }

    let markText = "";

    const forms = props.data.forms ?? [];
    const methods = props.data.methods ?? [];
    const answers = props.data.answers;

    if (props.filters?.includes("form_question")) {
      for (const [index, form] of forms.entries()) {
        markText += `### ${form.code.toUpperCase()} &nbsp; | &nbsp; ${form.name.toUpperCase()} \n`;
        markText += `<div style="font-size: 0.75em;">${form.description ?? ""}</div>\n`;

        const questions = form.questions ?? [];
        const formAnswer = answers?.metadata?.form[form.form_id] ?? {};
        const orders = form.metadata?.orders ?? [];

        for (const id of orders) {
          const question = questions.find((q) => q.question_id === id);

          if (!question) continue;

          if (question.type === "TITLE") {
            markText += `<div style="font-size: 0.875em; margin-top: 0.5em;">${question.name?.toUpperCase()}</div>\n`;
            markText += `<div style="font-size: 0.875em; margin-top: 0.5em;">${question.description ?? ""}</div>\n\n`;
          }

          if (question.type === "INPUT" || question.type === "TEXTAREA") {
            if (
              formAnswer[id] === null ||
              formAnswer[id] === undefined ||
              formAnswer[id] === ""
            ) {
              continue;
            }

            markText += `- **${question.name}:** &nbsp; ${formAnswer[id] ?? ""}\n`;
          }

          if (question.type === "CHECKBOX") {
            const checkboxes = formAnswer[id]?.filter(
              (answer: any) => answer.value === true,
            );

            if (checkboxes?.length) {
              markText += `- **${question.name}:** &nbsp;\n`;
              checkboxes?.forEach((answer: any) => {
                markText += `  - ${answer.option}\n`;
              });
            }
          }

          if (question.type === "RADIO") {
            if (
              formAnswer[id] === null ||
              formAnswer[id] === undefined ||
              formAnswer[id] === ""
            ) {
              continue;
            }

            markText += `- **${question.name}:** &nbsp;`;
            const radioAnswer = question.metadata?.options?.find(
              (option: any) => option.value === formAnswer[id],
            );
            markText += `${radioAnswer?.option ?? ""}\n`;
          }

          if (question.type === "PHOTO") {
            const photoAnswer = formAnswer[id];
            if (photoAnswer?.file_id) {
              markText += "\n&nbsp;\n";
              markText += `#### ${question.name.toUpperCase()}\n`;
              markText += `${question.description ?? ""}\n\n`;
              const response = await fileGet(photoAnswer.file_id);
              const { success, data } = response;
              if (success) {
                markText += `<img src="${data.infos},${data.base64}" style="width: auto; height: 200px;" />\n\n`;
              }
            }
          }
        }

        if (forms.length > index + 1) {
          markText += "---\n";
        }
      }
    }

    if (props.filters?.includes("method_question")) {
      if (methods.length) {
        markText += "---\n\n";
      }

      methods.forEach((method, index) => {
        markText += `### ${method.code.toUpperCase()} &nbsp; | &nbsp; ${method.name.toUpperCase()} \n`;
        const methodAnswers =
          answers?.metadata?.method?.[method.method_id] ?? {};

        method.questions?.forEach((item: any) => {
          const methodAnswer = methodAnswers?.[item.question_id];

          if (item.type === "TITLE") {
            markText += `<div style="font-size: 0.875em; margin-top: 0.5em;">${item.name?.toUpperCase()}</div>\n`;
            markText += `<div style="font-size: 0.875em; margin-top: 0.5em;">${item.description ?? ""}</div>\n\n`;
          }

          if (item.type === "INPUT" || item.type === "TEXTAREA") {
            markText += `- **${item.name}:**  ${methodAnswer ?? ""}\n`;
          }

          if (item.type === "CHECKBOX") {
            markText += `- **${item.name}:** &nbsp;\n`;
            methodAnswer
              ?.filter((answer: any) => !!answer.value)
              ?.forEach((answer: any) => {
                markText += `  - ${answer.option}\n`;
              });
          }

          if (item.type === "RADIO") {
            markText += `- **${item.name}:** &nbsp;`;
            const radioAnswer = item.metadata?.options?.find(
              (option: any) => option.value === methodAnswer,
            );
            markText += `${radioAnswer?.option ?? ""}\n`;
          }
        });

        if (methods.length > index + 1) {
          markText += "\n";
          markText += "---\n";
          markText += "\n";
        }
      });
    }

    let methodText = "";
    markText.split("\n").forEach((text) => {
      // Detectar se a linha contém uma imagem
      const hasImage = text.includes("<img") && text.includes("height:");
      let blockHeight = 0;

      if (hasImage) {
        // Extrair altura da imagem do style
        const heightMatch = text.match(/height:\s*(\d+)px/);
        const imageHeight = heightMatch ? parseInt(heightMatch[1]) : 200;
        // Adicionar margem extra para título e descrição da imagem
        blockHeight = imageHeight + 40; // 60px para título + descrição + margens
      } else {
        const pragraph = getMarkdownBlockHeights(text, {
          width: "617px",
          font_size: "0.75em",
          line_height: "normal",
        });
        blockHeight = (pragraph[0]?.height ?? 0) + 7;
      }

      if (!hasSpaceForContent(blockHeight)) {
        if (methodText.trim()) {
          pages.value[PAGE]!.content!.forms!.content = marked(methodText);
        }
        newPage("forms", "FORMS", null);
        methodText = `${text}\n`;
        HEIGHT_INTERNAL += blockHeight;
      } else {
        HEIGHT_INTERNAL += blockHeight;
        methodText += `${text}\n`;
      }
    });

    if (methodText.trim()) {
      pages.value[PAGE]!.content!.forms!.content = marked(methodText);
    }
  };

  const paginateSamples = () => {
    if (!props.data?.samples?.length) return;

    // Verificar espaço antes de adicionar título
    if (!hasSpaceForContent(heightTitle.value + minSpaceForContent.value)) {
      newPage("samples", "SAMPLES", t("report.titles.samples"));
    } else {
      HEIGHT_INTERNAL += heightTitle.value;
      pages.value[PAGE].content = {
        ...pages.value[PAGE].content,
        samples: {
          title: t("report.titles.samples"),
          type: "SAMPLES",
          break: false,
          content: "",
        },
      };
    }

    const samples = props.data.samples;

    let markText = "";

    for (const [index, sample] of samples.entries()) {
      markText += `- **${t("report.labels.name")}:** &nbsp; ${sample.name}\n`;
      markText += `- **${t("report.labels.code")}:** &nbsp; ${sample.code}\n`;
      if (sample.method) {
        markText += `- **${t("report.labels.method")}:** &nbsp;`;
        markText += ` ${sample.method.code} &nbsp; | &nbsp; ${sample.method.name}\n`;
      } else {
        markText += "\n";
      }
      markText += `- **${t("report.labels.validity_date")}:** &nbsp; ${getDate(sample.validity)}\n`;
      markText += `- **${t("report.labels.description")}:** &nbsp; ${sample.description ?? ""}\n`;

      if (samples.length > index + 1) {
        markText += "---\n";
      }
    }

    let methodText = "";
    markText.split("\n").forEach((text) => {
      const pragraph = getMarkdownBlockHeights(text, {
        width: "617px",
        font_size: "0.75em",
        line_height: "normal",
      });

      const blockHeight = (pragraph[0]?.height ?? 0) + 5;

      if (!hasSpaceForContent(blockHeight)) {
        if (methodText.trim()) {
          pages.value[PAGE]!.content!.samples!.content = marked(methodText);
        }
        newPage("samples", "SAMPLES", null);
        methodText = `${text}\n`;
        HEIGHT_INTERNAL += blockHeight;
      } else {
        HEIGHT_INTERNAL += blockHeight;
        methodText += `${text}\n`;
      }
    });

    if (methodText.trim()) {
      pages.value[PAGE]!.content!.samples!.content = marked(methodText);
    }
  };

  const paginateEquipments = () => {
    if (!props.data?.equipments?.length) return;

    // Verificar espaço antes de adicionar título
    if (!hasSpaceForContent(heightTitle.value + minSpaceForContent.value)) {
      newPage("equipments", "EQUIPMENTS", t("report.titles.equipments"));
    } else {
      HEIGHT_INTERNAL += heightTitle.value;
      pages.value[PAGE].content = {
        ...pages.value[PAGE].content,
        equipments: {
          title: t("report.titles.equipments"),
          type: "EQUIPMENTS",
          break: false,
          content: "",
        },
      };
    }

    const equipments = props.data.equipments;

    let markText = "";

    equipments?.forEach((item, index) => {
      markText += `<div style="display: flex; flex-direction: row; gap: 2em; align-items: flex-start; margin-top: 0.5em;">`;
      markText += `<div style="width: 100px; height: auto; background: #f5f5f5;">`;
      if (item.avatar) {
        markText += `<img src="${item.avatar.base64}" style="width: 100%; height: auto;" />`;
      }
      markText += `</div>`;

      markText += `<div style="display: flex; flex-direction: row; gap: 2em;">`;
      markText += `<ul style="list-style-type: none;">`;
      markText += `<li><strong>${t("report.labels.code")}:</strong> ${item.code}</li>`;
      markText += `<li><strong>${t("report.labels.name")}:</strong> ${item.name}</li>`;
      markText += `<li><strong>${t("report.labels.model")}:</strong> ${item.model}</li>`;
      markText += `<li><strong>${t("report.labels.brand")}:</strong> ${item.brand}</li>`;
      markText += `</ul>`;

      markText += `<ul style="list-style-type: none;">`;
      markText += `<li><strong>${t("report.labels.serial_number")}:</strong> ${item.serial_number}</li>`;
      markText += `<li><strong>${t("report.labels.fabrication_date")}:</strong> ${getDate(item.fabrication_date)}</li>`;
      markText += `<li><strong>${t("report.labels.validity_date")}:</strong> ${getDate(item.expired_date)}</li>`;
      markText += `</ul>`;
      markText += `</div>`;
      markText += `</div>\n`;

      if (equipments.length > index + 1) {
        markText += "<hr>\n";
      }
    });

    let methodText = "";
    markText.split("\n").forEach((text) => {
      const pragraph = getMarkdownBlockHeights(text, {
        width: "617px",
        font_size: "0.75em",
        line_height: "normal",
      });

      const blockHeight = (pragraph[0]?.height ?? 0) + 6;

      if (!hasSpaceForContent(blockHeight)) {
        if (methodText.trim()) {
          pages.value[PAGE]!.content!.equipments!.content = marked(methodText);
        }
        newPage("equipments", "EQUIPMENTS", null);
        methodText = `${text}\n`;
        HEIGHT_INTERNAL += blockHeight;
      } else {
        HEIGHT_INTERNAL += blockHeight;
        methodText += `${text}\n`;
      }
    });

    if (methodText.trim()) {
      pages.value[PAGE]!.content!.equipments!.content = marked(methodText);
    }
  };

  const paginateSigns = () => {
    if (!props.data?.signatures?.length) return;

    const SIGN_SIZE = 140; // Altura de cada assinatura
    const SIGN_GAP = 25; // Espaço entre assinaturas

    // Verificar espaço antes de adicionar título
    if (!hasSpaceForContent(heightTitle.value + SIGN_SIZE)) {
      newPage("signatures", "SIGNATURES", t("report.titles.signatures"));
    } else {
      HEIGHT_INTERNAL += heightTitle.value;
      pages.value[PAGE].content = {
        ...pages.value[PAGE].content,
        signatures: {
          title: t("report.titles.signatures"),
          type: "SIGNATURES",
          break: false,
          content: [],
        },
      };
    }

    const signs = props.data?.signatures ?? [];

    let SIGNATURES: any[] = [];
    let ITEMS = 0;

    signs.forEach((sign, index) => {
      ITEMS++;

      if (ITEMS === 1) {
        // Primeira assinatura da linha
        if (!hasSpaceForContent(SIGN_SIZE)) {
          // Não há espaço, salvar assinaturas anteriores e criar nova página
          if (SIGNATURES.length > 0) {
            pages.value[PAGE]!.content!.signatures!.content = [...SIGNATURES];
          }
          newPage("signatures", "SIGNATURES", null);
          SIGNATURES = [sign];
          HEIGHT_INTERNAL += SIGN_SIZE;
        } else {
          SIGNATURES.push(sign);
          HEIGHT_INTERNAL += SIGN_SIZE;
        }
      } else if (ITEMS === 2) {
        // Segunda assinatura da linha
        SIGNATURES.push(sign);

        // Se não for a última, adicionar gap para próxima linha
        if (signs.length > index + 1) {
          HEIGHT_INTERNAL += SIGN_GAP;
          ITEMS = 0; // Resetar contador para próxima linha
        }
      }
    });

    // Salvar assinaturas restantes
    if (SIGNATURES.length > 0) {
      pages.value[PAGE].content!.signatures!.content = [...SIGNATURES];
    }
  };

  // Sempre adicionar título da primeira página
  paginateTitle();

  // paginate company
  if (props.filters?.includes("company")) {
    paginateCompany();
  }

  // paginate analyse
  if (props.filters?.includes("analyse")) {
    paginateAnalyse();
  }

  // paginate employee
  if (props.filters?.includes("employee")) {
    paginateEmployee();
  }

  // paginate methods
  if (props.filters?.includes("method")) {
    paginateMethod();
  }

  // paginate field memory
  if (
    props.filters?.includes("form_question") ||
    props.filters?.includes("method_question")
  ) {
    await paginateForm();
  }

  // paginate samples
  if (props.filters?.includes("sample")) {
    paginateSamples();
  }

  // paginate equipments
  if (props.filters?.includes("equipment")) {
    paginateEquipments();
  }

  // paginate signatures
  if (props.filters?.includes("signature")) {
    paginateSigns();
  }
}

watch(
  () => props.data,
  () => paginate(),
);

onMounted(() => {
  paginate();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(pages.value, (page) => {
    return (_openBlock(), _createBlock(_unref(pageExport), {
      key: page.page,
      width: width.value,
      page: page.page + 1,
      scale: __props.scale,
      "show-footer": __props.footer?.visible,
      "footer-title": __props.footer?.title,
      "footer-subtitle": __props.footer?.subtitle
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(Object.values(page.content!), (item, index) => {
            return (_openBlock(), _createElementBlock(_Fragment, { key: index }, [
              (item!.title)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 0,
                    class: "page-title",
                    innerHTML: item!.title
                  }, null, 8, _hoisted_2))
                : _createCommentVNode("", true),
              (item!.type === 'COMPANY')
                ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                    _createElementVNode("div", _hoisted_4, [
                      (item!.content.avatar)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                            _createElementVNode("img", {
                              src: item!.content.avatar.base64
                            }, null, 8, _hoisted_6)
                          ]))
                        : _createCommentVNode("", true),
                      _createElementVNode("div", _hoisted_7, [
                        (item!.content.name)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_8, _toDisplayString(item!.content.name), 1))
                          : _createCommentVNode("", true),
                        (item!.content.document)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_9, _toDisplayString(item!.content.document), 1))
                          : _createCommentVNode("", true),
                        (item!.content.address)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_10, _toDisplayString(item!.content.address), 1))
                          : _createCommentVNode("", true),
                        (item!.content.contact)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_11, _toDisplayString(item!.content.contact), 1))
                          : _createCommentVNode("", true)
                      ])
                    ])
                  ]))
                : _createCommentVNode("", true),
              (item!.type === 'ANALYSE')
                ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                    _createElementVNode("div", _hoisted_13, [
                      _createElementVNode("div", {
                        innerHTML: item!.content
                      }, null, 8, _hoisted_14)
                    ])
                  ]))
                : _createCommentVNode("", true),
              (item!.type === 'EMPLOYEE')
                ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                    _createElementVNode("div", _hoisted_16, [
                      _createElementVNode("div", {
                        innerHTML: item!.content
                      }, null, 8, _hoisted_17)
                    ])
                  ]))
                : _createCommentVNode("", true),
              (item!.type === 'METHODS')
                ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                    _createElementVNode("div", _hoisted_19, [
                      _createElementVNode("div", {
                        innerHTML: item!.content
                      }, null, 8, _hoisted_20)
                    ])
                  ]))
                : _createCommentVNode("", true),
              (item!.type === 'FORMS')
                ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
                    _createElementVNode("div", _hoisted_22, [
                      _createElementVNode("div", {
                        innerHTML: item!.content
                      }, null, 8, _hoisted_23)
                    ])
                  ]))
                : _createCommentVNode("", true),
              (item!.type === 'SAMPLES')
                ? (_openBlock(), _createElementBlock("div", _hoisted_24, [
                    _createElementVNode("div", _hoisted_25, [
                      _createElementVNode("div", {
                        innerHTML: item!.content
                      }, null, 8, _hoisted_26)
                    ])
                  ]))
                : _createCommentVNode("", true),
              (item!.type === 'EQUIPMENTS')
                ? (_openBlock(), _createElementBlock("div", _hoisted_27, [
                    _createElementVNode("div", _hoisted_28, [
                      _createElementVNode("div", {
                        innerHTML: item!.content
                      }, null, 8, _hoisted_29)
                    ])
                  ]))
                : _createCommentVNode("", true),
              (item!.type === 'SIGNATURES')
                ? (_openBlock(), _createElementBlock("div", _hoisted_30, [
                    _createElementVNode("div", _hoisted_31, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(item!.content, (sign) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: sign.signature_id,
                          class: "signature"
                        }, [
                          _createElementVNode("div", _hoisted_32, [
                            (sign.signature)
                              ? (_openBlock(), _createElementBlock("img", {
                                  key: 0,
                                  src: sign.signature,
                                  alt: "Signature"
                                }, null, 8, _hoisted_33))
                              : (_openBlock(), _createElementBlock("img", {
                                  key: 1,
                                  src: require('@/assets/images/signature.png'),
                                  style: {"opacity":"0.25"}
                                }, null, 8, _hoisted_34))
                          ]),
                          _createElementVNode("div", _hoisted_35, _toDisplayString(sign.name), 1),
                          _createElementVNode("div", _hoisted_36, [
                            (sign.type === 'OTHER')
                              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                  _createTextVNode(_toDisplayString(sign.type_name), 1)
                                ], 64))
                              : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                  _createTextVNode(_toDisplayString(_unref(t)(`common.labels.${sign.type.toLowerCase()}`)), 1)
                                ], 64))
                          ]),
                          _createElementVNode("div", _hoisted_37, _toDisplayString(sign.date), 1),
                          _createElementVNode("div", _hoisted_38, _toDisplayString(sign.document_name) + " " + _toDisplayString(sign.document_number), 1)
                        ]))
                      }), 128))
                    ])
                  ]))
                : _createCommentVNode("", true)
            ], 64))
          }), 128))
        ])
      ]),
      _: 2
    }, 1032, ["width", "page", "scale", "show-footer", "footer-title", "footer-subtitle"]))
  }), 128))
}
}

})